import { create } from "zustand";
import type { MainButtonState } from "./types";

interface BackState {
  visible: boolean;
  onClick: (() => void) | null;
}

interface IosUiState {
  back: BackState;
  main: MainButtonState | null;
  /** Show the back affordance; returns a cleanup that hides it. */
  showBack(onClick: () => void): () => void;
  /** Apply the full main-button state; returns a cleanup that hides it. */
  setMain(state: MainButtonState): () => void;
}

/**
 * UI state for the iOS-native back control and main-button bar
 * (`<IosBackControl/>` / `<IosMainButtonBar/>`, Task 6) since Capacitor has
 * no native chrome equivalent to Telegram's BackButton/MainButton — those
 * are rendered in React from this store instead.
 *
 * React effects (`useBackButton`/`useMainButton` call sites) can re-run out
 * of order: a screen unmounting can run its cleanup *after* the next
 * screen's effect has already called `show`/`set` again. Each cleanup below
 * only clears the store if it is still the handler/state it originally set
 * — an identity check that makes stale cleanups no-ops instead of hiding
 * the newer screen's back button or main button.
 */
export const useIosUiStore = create<IosUiState>((set, get) => ({
  back: { visible: false, onClick: null },
  main: null,
  showBack: (onClick) => {
    set({ back: { visible: true, onClick } });
    return () => {
      if (get().back.onClick === onClick) {
        set({ back: { visible: false, onClick: null } });
      }
    };
  },
  setMain: (state) => {
    set({ main: state });
    return () => {
      if (get().main === state) {
        set({ main: null });
      }
    };
  },
}));
